import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Video, VideoOff, Mic, MicOff, PhoneOff, Phone, Minimize2, Maximize2, SwitchCamera } from 'lucide-react';
import Peer from 'simple-peer';
import { socket } from '../../services/socket';
import { getIceServers } from '../../services/webrtcService';
import { useAuth } from '../../context/AuthContext';
import { debugLog, debugWarn, debugError } from '../../utils/logger';

// Estados de ICE que indican que el media path esta roto y conviene intentar
// recuperar la conexion (ICE restart) en vez de dejar la llamada congelada.
const ICE_UNHEALTHY_STATES = ['disconnected', 'failed'];
const MAX_RECONNECT_ATTEMPTS = 3;
const FREEZE_CHECK_MS = 4000;
const ICE_RESTART_GRACE_MS = 8000;

// El boton de cambiar camara solo tiene sentido en moviles/tablets: en un
// escritorio con varias webcams cambiar de "facingMode" no aplica y el control
// solo estorbaria. Se combina el hint nativo de userAgentData con el UA clasico
// y la presencia de tactil (iPadOS se anuncia como Macintosh desde iOS 13).
function isMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  if (navigator.userAgentData?.mobile) return true;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPod|IEMobile|Opera Mini|Mobile/i.test(ua)) return true;
  const isIPadOS = /Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1;
  return isIPadOS || /iPad/i.test(ua);
}

// `initialIncomingCall` es el payload de 'incomingCall' que ya recibió el
// receptor global (VideoCallProvider) antes de que este modal existiera. Sin
// sembrarlo aquí se perdería la oferta SDP entre el timbre y el montaje.
function VideoCallModal({ appointment, otherUser, onClose, initialIncomingCall = null }) {
  const { user } = useAuth();
  const [stream, setStream] = useState(null);
  const [receivingCall, setReceivingCall] = useState(!!initialIncomingCall);
  const [callerSignal, setCallerSignal] = useState(initialIncomingCall);
  // Último error de enrutado devuelto por el backend ('callUser:error').
  const [callError, setCallError] = useState(null);
  const [callAccepted, setCallAccepted] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [isCalling, setIsCalling] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [facingMode, setFacingMode] = useState('user');
  const [canSwitchCamera, setCanSwitchCamera] = useState(false);
  const [switchingCamera, setSwitchingCamera] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  // Minimizar: reduce el video remoto a un PiP flotante para liberar la pantalla
  // (p. ej. para consultar la historia clínica). NO interrumpe el stream/peer;
  // solo cambia el layout visual (los <video> permanecen montados).
  const [minimized, setMinimized] = useState(false);
  // Salud de la conexion ICE, para feedback visual ('stable' | 'reconnecting' | 'failed').
  const [connectionHealth, setConnectionHealth] = useState('stable');

  const myVideo = useRef();
  const userVideo = useRef();
  const connectionRef = useRef(null);
  const streamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const roomId = appointment._id;
  const handlersSetup = useRef(false);
  // Credenciales STUN/TURN (Twilio NTS) precargadas al montar el modal.
  const iceServersRef = useRef(null);

  // ─── Señalización y recuperación ─────────────────────────────────────────
  // Rol local: determina quien puede crear ofertas (solo el iniciador).
  const peerRoleRef = useRef(null); // 'initiator' | 'receiver'
  // socket.id del otro extremo. Imprescindible para enrutar los candidatos ICE:
  // el iniciador solo lo conoce cuando llega 'callAccepted'.
  const remoteSocketIdRef = useRef(initialIncomingCall?.from || null);
  // Distingue el SDP del handshake inicial de las renegociaciones posteriores.
  const initialSdpSentRef = useRef(false);
  // Candidatos propios generados antes de conocer remoteSocketId.
  const pendingLocalCandidatesRef = useRef([]);
  // Señales remotas llegadas antes de que exista el peer (p. ej. durante el
  // await de las credenciales ICE).
  const pendingRemoteSignalsRef = useRef([]);
  // Marca una peticion de ICE restart hecha por el NO iniciador.
  const pendingIceRestartRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const freezeWatchdogRef = useRef(null);
  const lastVideoTimeRef = useRef(0);
  // Indireccion para poder invocar el reintento desde listeners creados antes
  // de que la funcion exista, sin arrastrar closures obsoletas.
  const attemptIceRestartRef = useRef(() => {});
  // Espejo de callAccepted accesible desde closures de larga vida (watchdog).
  const callAcceptedRef = useRef(false);

  useEffect(() => { callAcceptedRef.current = callAccepted; }, [callAccepted]);

  // ─── Precargar credenciales STUN/TURN ────────────────────────────────────
  // Se piden al montar y no al iniciar la llamada, para no meter un round-trip
  // HTTP en el momento de crear el peer. getIceServers() nunca lanza: ante
  // cualquier fallo devuelve un fallback STUN-only.
  useEffect(() => {
    let mounted = true;
    getIceServers().then((servers) => {
      if (mounted) iceServersRef.current = servers;
    });
    return () => { mounted = false; };
  }, []);

  // Devuelve las credenciales precargadas; si el prefetch aún no terminó
  // (el usuario llamó muy rápido), las pide en ese momento.
  const resolveIceServers = useCallback(async () => {
    if (iceServersRef.current) return iceServersRef.current;
    const servers = await getIceServers();
    iceServersRef.current = servers;
    return servers;
  }, []);

  // ─── Envío de candidatos ICE (trickle) ───────────────────────────────────
  const emitIceCandidate = useCallback((candidate) => {
    socket.emit('ice-candidate', {
      to: remoteSocketIdRef.current,
      appointmentId: roomId,
      candidate
    });
  }, [roomId]);

  const sendIceCandidate = useCallback((candidate) => {
    // El iniciador genera candidatos antes de saber a que socket enviarlos
    // (solo lo sabe al recibir 'callAccepted'). Encolarlos evita perder la
    // mitad de la señalizacion y dejar al otro extremo sin candidatos remotos.
    if (!remoteSocketIdRef.current) {
      pendingLocalCandidatesRef.current.push(candidate);
      return;
    }
    emitIceCandidate(candidate);
  }, [emitIceCandidate]);

  const flushLocalCandidates = useCallback(() => {
    if (!remoteSocketIdRef.current) return;
    const queued = pendingLocalCandidatesRef.current;
    pendingLocalCandidatesRef.current = [];
    if (queued.length) debugLog(`🧊 Enviando ${queued.length} candidato(s) ICE en cola`);
    queued.forEach(emitIceCandidate);
  }, [emitIceCandidate]);

  // ─── Recepción de señales remotas ────────────────────────────────────────
  const applyRemoteSignal = useCallback((signal) => {
    const peer = connectionRef.current;
    // Aun no hay peer (se esta resolviendo el ICE o el usuario no ha
    // contestado): encolar en vez de descartar.
    if (!peer) {
      pendingRemoteSignalsRef.current.push(signal);
      return;
    }
    try {
      peer.signal(signal);
    } catch (err) {
      debugWarn('⚠️ Error aplicando señal remota:', err.message);
    }
  }, []);

  const flushRemoteSignals = useCallback(() => {
    const peer = connectionRef.current;
    if (!peer) return;
    const queued = pendingRemoteSignalsRef.current;
    pendingRemoteSignalsRef.current = [];
    if (queued.length) debugLog(`🧊 Aplicando ${queued.length} señal(es) remota(s) en cola`);
    queued.forEach((signal) => {
      try {
        peer.signal(signal);
      } catch (err) {
        debugWarn('⚠️ Error aplicando señal remota en cola:', err.message);
      }
    });
  }, []);

  // ─── Enrutado de las señales locales ─────────────────────────────────────
  // Con trickle habilitado, simple-peer emite 'signal' tanto para el SDP como
  // para cada candidato y para las peticiones de renegociacion. Cada tipo va
  // por un canal distinto; en particular la renegociacion NO puede ir por
  // 'callUser', porque el otro extremo la mostraria como llamada entrante nueva.
  const routeLocalSignal = useCallback((signal, targetUserId) => {
    if (signal?.candidate) {
      sendIceCandidate(signal);
      return;
    }

    const role = peerRoleRef.current;
    const isInitialSdp = !initialSdpSentRef.current && (
      (role === 'initiator' && signal?.type === 'offer') ||
      (role === 'receiver' && signal?.type === 'answer')
    );

    if (isInitialSdp) {
      initialSdpSentRef.current = true;
      if (role === 'initiator') {
        debugLog('📤 Enviando oferta SDP inicial (callUser)');
        socket.emit('callUser', {
          userToCall: targetUserId,
          signalData: signal,
          from: user._id || user.id,
          fromName: `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`,
          appointmentId: appointment._id
        });
      } else {
        debugLog('📤 Enviando respuesta SDP inicial (answerCall)');
        // appointmentId: el backend marca con él el inicio real de la llamada.
        socket.emit('answerCall', { signal, to: remoteSocketIdRef.current, appointmentId: roomId });
      }
      return;
    }

    debugLog('🔄 Enviando señal de renegociación:', signal?.type || 'renegotiate');
    socket.emit('webrtc:renegotiate', {
      to: remoteSocketIdRef.current,
      appointmentId: roomId,
      signal,
      iceRestart: pendingIceRestartRef.current
    });
    pendingIceRestartRef.current = false;
  }, [sendIceCandidate, roomId, user, appointment]);

  // ─── Obtener stream local ────────────────────────────────────────────────
  useEffect(() => {
    // fix(lint): copiar refs a variables locales para el cleanup
    const myVideoEl = myVideo.current;
    const userVideoEl = userVideo.current;

    debugLog('\n🎥 [VideoCallModal] Iniciando componente');
    debugLog('🎫 Appointment ID:', roomId);
    debugLog('👤 User:', user?.profile?.firstName);

    navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
        aspectRatio: { ideal: 16 / 9 },
        facingMode: 'user',
        frameRate: { ideal: 30, max: 30 }
      },
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    })
      .then((currentStream) => {
        debugLog('📹 Stream local obtenido');
        setStream(currentStream);
        streamRef.current = currentStream;

        // enumerateDevices solo revela las camaras reales una vez concedido el
        // permiso, por eso se consulta despues del getUserMedia.
        if (!isMobileDevice() || !navigator.mediaDevices?.enumerateDevices) return;
        navigator.mediaDevices.enumerateDevices()
          .then((devices) => {
            const cameras = devices.filter((d) => d.kind === 'videoinput');
            debugLog('📷 Camaras detectadas:', cameras.length);
            setCanSwitchCamera(cameras.length > 1);
          })
          .catch((err) => debugWarn('No se pudieron enumerar los dispositivos:', err));
      })
      .catch((err) => {
        debugError('❌ Error accediendo a medios:', err);
        alert('No se pudo acceder a la cámara o micrófono');
      });

    debugLog('📡 Emitiendo video:join');
    socket.emit('video:join', roomId);

    // En móvil la pestaña se suspende al cambiar de app y el socket cae con un
    // id nuevo al volver. Sin re-emitir el join, el usuario queda fuera de la
    // sala y el backend descarta toda su señalización ICE (mismo patrón que ya
    // aplica ChatBox con 'chat:join').
    const handleReconnect = () => {
      debugLog('📤 [reconnect] Re-emitiendo video:join para', roomId);
      socket.emit('video:join', roomId);
    };
    socket.on('connect', handleReconnect);

    return () => {
      socket.off('connect', handleReconnect);
      debugLog('\n🧹 [VideoCallModal] Limpiando componente');
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      // usar variables locales capturadas al inicio del efecto
      if (myVideoEl) myVideoEl.srcObject = null;
      if (userVideoEl) userVideoEl.srcObject = null;
      if (connectionRef.current) {
        try { connectionRef.current.destroy(); } catch (_) {}
        connectionRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (freezeWatchdogRef.current) {
        clearInterval(freezeWatchdogRef.current);
        freezeWatchdogRef.current = null;
      }
      socket.emit('video:leave', roomId);
      // Los listeners de señalizacion los desmonta su propio efecto, con la
      // referencia concreta del handler. Un socket.off(evento) a secas aqui los
      // borraria sin que aquel volviera a registrarlos (sus deps no cambian).
    };
  }, [roomId, user]);

  // ─── Asignar stream local al <video> propio ──────────────────────────────
  useEffect(() => {
    // fix(lint): copiar ref a variable local para que el cleanup
    // no acceda a myVideo.current si el nodo ya fue desmontado
    const videoEl = myVideo.current;
    if (stream && videoEl) {
      debugLog('🎥 Asignando stream local a myVideo');
      videoEl.srcObject = stream;
    }
    return () => {
      if (videoEl) videoEl.srcObject = null;
    };
  }, [stream]);

  // ─── FIX VIDEO REMOTO ────────────────────────────────────────────────────
  // peer.on('stream') se disparaba cuando callAccepted=false y el <video>
  // remoto aún no existía en el DOM. Guardamos el stream en remoteStreamRef
  // y lo asignamos aquí, cuando callAccepted=true garantiza que el elemento
  // ya está montado.
  useEffect(() => {
    if (callAccepted && !callEnded && remoteStreamRef.current && userVideo.current) {
      debugLog('📺 Asignando stream remoto a userVideo (callAccepted=true)');
      userVideo.current.srcObject = remoteStreamRef.current;
    }
  }, [callAccepted, callEnded]);

  // ─── handleEndCall ───────────────────────────────────────────────────────
  const handleEndCall = useCallback(() => {
    debugLog('\n📴 [handleEndCall] Finalizando llamada');
    const currentStream = streamRef.current || stream;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (freezeWatchdogRef.current) {
      clearInterval(freezeWatchdogRef.current);
      freezeWatchdogRef.current = null;
    }
    remoteSocketIdRef.current = null;
    initialSdpSentRef.current = false;
    pendingLocalCandidatesRef.current = [];
    pendingRemoteSignalsRef.current = [];
    pendingIceRestartRef.current = false;
    reconnectAttemptsRef.current = 0;
    peerRoleRef.current = null;

    if (currentStream) {
      currentStream.getTracks().forEach(track => track.stop());
      if (myVideo.current) myVideo.current.srcObject = null;
      if (userVideo.current) userVideo.current.srcObject = null;
      setStream(null);
      streamRef.current = null;
    }

    if (connectionRef.current) {
      try { connectionRef.current.destroy(); } catch (err) { debugError('Error al destruir peer:', err); }
      connectionRef.current = null;
    }

    remoteStreamRef.current = null;

    try {
      socket.emit('endCall', { roomId, appointmentId: appointment._id });
    } catch (err) {
      debugError('Error enviando endCall:', err);
    }

    setCallEnded(true);
    setCallAccepted(false);
    setReceivingCall(false);
    setIsCalling(false);
    setTimeout(() => onClose(), 300);
  }, [stream, roomId, appointment, onClose]);

  // ─── Watchdog: detectar video remoto congelado ───────────────────────────
  // Si currentTime del <video> remoto deja de avanzar mientras ICE sigue en
  // 'connected', el media path esta muerto aunque la señalizacion siga viva
  // (caso tipico: el relay TURN se cae a mitad de consulta).
  const startFreezeWatchdog = useCallback(() => {
    if (freezeWatchdogRef.current) clearInterval(freezeWatchdogRef.current);
    lastVideoTimeRef.current = 0;

    freezeWatchdogRef.current = setInterval(() => {
      const pc = connectionRef.current?._pc;
      const videoEl = userVideo.current;
      // callAcceptedRef y no el estado: este callback se crea dentro de un
      // listener registrado al construir el peer, cuando callAccepted todavia
      // era false, y una closure lo dejaria congelado en ese valor para siempre.
      if (!pc || !videoEl || !callAcceptedRef.current) return;

      const currentTime = videoEl.currentTime;
      const isFrozen = currentTime === lastVideoTimeRef.current;
      lastVideoTimeRef.current = currentTime;

      if (pc.iceConnectionState === 'connected' && isFrozen && currentTime > 0) {
        debugWarn('🥶 [watchdog] Video remoto sin avanzar pese a ICE "connected"');
        attemptIceRestartRef.current();
      }
    }, FREEZE_CHECK_MS);
  }, []);

  // ─── Intentar recuperar la conexión (ICE restart) ────────────────────────
  const attemptIceRestart = useCallback(() => {
    const peer = connectionRef.current;
    const pc = peer?._pc;
    if (!peer || !pc || peer.destroyed) return;

    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      debugError('🚫 [attemptIceRestart] Máximo de reintentos alcanzado, finalizando llamada');
      setConnectionHealth('failed');
      handleEndCall();
      return;
    }

    reconnectAttemptsRef.current += 1;
    setConnectionHealth('reconnecting');
    debugWarn(`🔄 [attemptIceRestart] Intento #${reconnectAttemptsRef.current}`);

    try {
      if (peerRoleRef.current === 'initiator') {
        if (typeof pc.restartIce === 'function') pc.restartIce();
        // simple-peer NO escucha 'negotiationneeded', asi que restartIce() por
        // si solo jamas produce una oferta nueva: hay que pedir la
        // renegociacion explicitamente.
        peer.negotiate();
      } else {
        // El no-iniciador no crea ofertas. negotiate() emite una señal
        // {type:'renegotiate'} que enviamos marcada con iceRestart para que el
        // iniciador reinicie ICE por ambos.
        pendingIceRestartRef.current = true;
        peer.negotiate();
      }
    } catch (err) {
      debugError('❌ [attemptIceRestart] Error al reiniciar ICE:', err.message);
    }

    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    reconnectTimeoutRef.current = setTimeout(() => {
      const state = connectionRef.current?._pc?.iceConnectionState;
      if (state === 'connected' || state === 'completed') {
        debugLog('✅ [attemptIceRestart] Conexión recuperada');
        setConnectionHealth('stable');
        reconnectAttemptsRef.current = 0;
      } else {
        attemptIceRestartRef.current();
      }
    }, ICE_RESTART_GRACE_MS);
  }, [handleEndCall]);

  useEffect(() => { attemptIceRestartRef.current = attemptIceRestart; }, [attemptIceRestart]);

  // ─── Listeners de salud ICE sobre el RTCPeerConnection ───────────────────
  const attachIceHealthListeners = useCallback((peer) => {
    const pc = peer?._pc;
    if (!pc || typeof pc.addEventListener !== 'function') return;

    // IMPORTANTE: addEventListener, NO `pc.oniceconnectionstatechange = ...`.
    // simple-peer usa esa propiedad para su propia maquina de estados
    // (_onIceStateChange marca _pcReady y emite 'connect'; destruye el peer en
    // 'failed'/'closed'). Sobrescribirla la deja inservible.
    pc.addEventListener('iceconnectionstatechange', () => {
      const state = pc.iceConnectionState;
      debugLog('🧊 [ICE] iceConnectionState:', state);

      if (state === 'connected' || state === 'completed') {
        setConnectionHealth('stable');
        reconnectAttemptsRef.current = 0;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        startFreezeWatchdog();
      } else if (ICE_UNHEALTHY_STATES.includes(state)) {
        debugWarn('⚠️ [ICE] Estado no saludable:', state, '— intentando recuperar');
        setConnectionHealth('reconnecting');
        attemptIceRestartRef.current();
      }
    });

    pc.addEventListener('connectionstatechange', () => {
      debugLog('🔗 [PC] connectionState:', pc.connectionState);
    });
  }, [startFreezeWatchdog]);

  // ─── Listeners (incomingCall, callAccepted, roomUsersUpdate) ────────────
  useEffect(() => {
    if (handlersSetup.current) return;
    handlersSetup.current = true;

    const onIncomingCall = (data) => {
      // `data.from` es un socket.id y `user._id` un id de usuario: comparados
      // entre sí nunca coinciden, así que esta guarda jamás llegó a filtrar
      // nada. El backend manda ahora `fromUserId`, que sí es comparable.
      const myUserId = String(user?._id || user?.id || '');
      if (data.fromUserId && String(data.fromUserId) === myUserId) {
        debugWarn('⚠️ Ignorando llamada de uno mismo');
        return;
      }
      debugLog('\n📞 [incomingCall] Llamada entrante de:', data.fromName);
      // Ya conocemos el socket del otro extremo: necesario para enrutarle
      // nuestros candidatos ICE en cuanto contestemos.
      if (data.from) remoteSocketIdRef.current = data.from;
      setReceivingCall(true);
      setCallerSignal(data);
    };

    const onCallAccepted = (data) => {
      debugLog('\n✅ [callAccepted] Señal de respuesta recibida');
      setCallAccepted(true);
      // Momento en que el INICIADOR descubre el socket destino. Sin esto sus
      // candidatos ICE se emitirian con to=undefined y el backend los
      // descartaria, dejando al otro extremo sin candidatos remotos.
      if (data.from) {
        remoteSocketIdRef.current = data.from;
        flushLocalCandidates();
      }
      if (connectionRef.current && data.signal) {
        try {
          const state = connectionRef.current._pc?.signalingState;
          if (state === 'have-local-offer') {
            connectionRef.current.signal(data.signal);
          } else {
            debugWarn('⚠️ Peer en estado:', state, '- No se puede hacer signal');
          }
        } catch (err) {
          debugError('❌ Error en signal:', err.message);
        }
      }
    };

    // Trickle ICE: candidatos individuales reenviados por el backend.
    const onIceCandidate = (data) => {
      if (!data?.candidate) return;
      applyRemoteSignal(data.candidate);
    };

    // Renegociacion sobre una llamada YA establecida (ICE restart, cambios de
    // pista). Canal separado de 'callUser' a proposito: por ahi el remoto lo
    // interpretaria como una llamada entrante nueva y mostraria el panel de
    // "Llamada entrante" en mitad de la consulta.
    const onRenegotiate = (data) => {
      if (!data?.signal) return;
      debugLog('🔄 [webrtc:renegotiate] Señal de renegociación recibida');
      const peer = connectionRef.current;
      if (peer && data.iceRestart && peerRoleRef.current === 'initiator') {
        const pc = peer._pc;
        if (pc && typeof pc.restartIce === 'function') {
          debugLog('🔄 ICE restart solicitado por el otro extremo');
          pc.restartIce();
        }
      }
      // Sin peer todavia, applyRemoteSignal encola en vez de descartar.
      applyRemoteSignal(data.signal);
    };

    const onRoomUsersUpdate = (data) => {
      debugLog('\n👥 [roomUsersUpdate] Usuarios en sala:', data.usersCount);
    };

    // El backend rechaza el enrutado (destino desconectado, cita que no
    // corresponde...). Sin este listener quien llamaba se quedaba en
    // "Esperando respuesta" para siempre, sin ninguna pista del motivo.
    const onCallUserError = (data) => {
      debugWarn('⚠️ [callUser:error]', data?.message);
      setIsCalling(false);
      setCallError(data?.message || 'No se pudo iniciar la llamada');
    };

    socket.on('incomingCall', onIncomingCall);
    socket.on('callUser:error', onCallUserError);
    socket.on('callAccepted', onCallAccepted);
    socket.on('ice-candidate', onIceCandidate);
    socket.on('webrtc:renegotiate', onRenegotiate);
    socket.on('roomUsersUpdate', onRoomUsersUpdate);

    return () => {
      handlersSetup.current = false;
      // `off` con referencia concreta: un off(evento) a secas borraria tambien
      // los listeners registrados por otros efectos sobre el mismo evento.
      socket.off('incomingCall', onIncomingCall);
      socket.off('callUser:error', onCallUserError);
      socket.off('callAccepted', onCallAccepted);
      socket.off('ice-candidate', onIceCandidate);
      socket.off('webrtc:renegotiate', onRenegotiate);
      socket.off('roomUsersUpdate', onRoomUsersUpdate);
    };
  }, [user, applyRemoteSignal, flushLocalCandidates]);

  // ─── Listener callEnded (separado para incluir handleEndCall en deps) ───
  useEffect(() => {
    const onCallEnded = () => {
      debugLog('\n📴 [callEnded] Otro usuario terminó la llamada');
      handleEndCall();
    };
    socket.on('callEnded', onCallEnded);
    return () => socket.off('callEnded', onCallEnded);
  }, [handleEndCall]);

  // ─── Iniciar llamada ─────────────────────────────────────────────────────
  const callUser = async () => {
    debugLog('\n📞 [callUser] Iniciando llamada');
    setCallError(null);
    setIsCalling(true);
    peerRoleRef.current = 'initiator';
    initialSdpSentRef.current = false;

    const targetUserId = otherUser?.user?._id || otherUser?.user?.id || otherUser?._id || otherUser?.id;
    debugLog('🎯 [callUser] Target user ID:', targetUserId);

    const iceServers = await resolveIceServers();

    const peer = new Peer({
      initiator: true,
      // Trickle ICE: los candidatos viajan incrementalmente en vez de esperar a
      // que termine la recoleccion completa antes de enviar el SDP. Conecta
      // antes y tolera que algun candidato concreto falle.
      trickle: true,
      stream,
      config: { iceServers, iceTransportPolicy: 'all' }
    });

    // Se adjuntan ya: _pc existe desde el constructor y asi no perdemos
    // ninguna transicion de estado previa a 'connect'.
    attachIceHealthListeners(peer);

    peer.on('signal', (signal) => routeLocalSignal(signal, targetUserId));

    peer.on('stream', (remoteStream) => {
      debugLog('📥 Stream remoto recibido (callUser) — guardando en ref');
      remoteStreamRef.current = remoteStream;
      if (userVideo.current) {
        userVideo.current.srcObject = remoteStream;
      }
    });

    peer.on('error', (err) => {
      debugError('❌ Error Peer (callUser):', err);
      setIsCalling(false);
    });

    connectionRef.current = peer;
    // Señales que llegaron mientras se resolvian las credenciales ICE.
    flushRemoteSignals();
  };

  // ─── Contestar llamada ───────────────────────────────────────────────────
  const answerCall = async () => {
    debugLog('\n✅ [answerCall] Aceptando llamada');
    setCallAccepted(true);
    setReceivingCall(false);
    peerRoleRef.current = 'receiver';
    initialSdpSentRef.current = false;
    // Ya deberia venir de 'incomingCall'; lo reafirmamos por si el panel se
    // rehidrato desde callerSignal.
    if (callerSignal?.from) remoteSocketIdRef.current = callerSignal.from;

    const iceServers = await resolveIceServers();

    const peer = new Peer({
      initiator: false,
      trickle: true,
      stream,
      config: { iceServers, iceTransportPolicy: 'all' }
    });

    attachIceHealthListeners(peer);

    peer.on('signal', (signal) => routeLocalSignal(signal, null));

    peer.on('stream', (remoteStream) => {
      debugLog('📥 Stream remoto recibido (answerCall) — guardando en ref');
      remoteStreamRef.current = remoteStream;
      if (userVideo.current) {
        userVideo.current.srcObject = remoteStream;
      }
    });

    peer.on('error', (err) => debugError('❌ Error Peer (answerCall):', err));

    connectionRef.current = peer;

    if (callerSignal?.signal) {
      peer.signal(callerSignal.signal);
    } else if (callerSignal) {
      peer.signal(callerSignal);
    }

    // Candidatos del iniciador que llegaron antes de contestar.
    flushRemoteSignals();
    // Y candidatos propios encolados si el peer genero alguno antes de tiempo.
    flushLocalCandidates();
  };

  // ─── Controles de media ──────────────────────────────────────────────────
  const toggleVideo = () => {
    const track = stream?.getVideoTracks()[0];
    if (track) { track.enabled = !track.enabled; setVideoEnabled(track.enabled); }
  };

  const toggleAudio = () => {
    const track = stream?.getAudioTracks()[0];
    if (track) { track.enabled = !track.enabled; setAudioEnabled(track.enabled); }
  };

  // ─── Cambiar entre camara frontal y trasera (solo movil) ─────────────────
  // Se pide unicamente video: el track de audio actual se conserva para no
  // cortar el micro ni obligar a renegociar la parte de audio.
  const requestVideoTrack = (mode, exact) => navigator.mediaDevices.getUserMedia({
    video: {
      width: { ideal: 1280, max: 1920 },
      height: { ideal: 720, max: 1080 },
      aspectRatio: { ideal: 16 / 9 },
      facingMode: exact ? { exact: mode } : mode,
      frameRate: { ideal: 30, max: 30 }
    }
  }).then((s) => s.getVideoTracks()[0]);

  const switchCamera = async () => {
    const currentStream = streamRef.current;
    const oldTrack = currentStream?.getVideoTracks()[0];
    if (!currentStream || !oldTrack || switchingCamera) return;

    const nextMode = facingMode === 'user' ? 'environment' : 'user';
    setSwitchingCamera(true);
    debugLog('🔄 [switchCamera] Cambiando a facingMode:', nextMode);

    // iOS libera la camara de forma perezosa: si no se detiene la pista actual
    // antes de pedir la otra, el nuevo getUserMedia devuelve un video negro.
    oldTrack.stop();

    let newTrack = null;
    let appliedMode = nextMode;
    try {
      // `exact` garantiza la camara pedida; si el dispositivo no la expone asi
      // se reintenta como preferencia (puede devolver la misma camara).
      newTrack = await requestVideoTrack(nextMode, true);
    } catch (err) {
      debugWarn('facingMode exact no disponible, reintentando como ideal:', err);
      try {
        newTrack = await requestVideoTrack(nextMode, false);
      } catch (err2) {
        debugError('❌ No se pudo cambiar de camara:', err2);
        try {
          // Recuperar la camara anterior para no dejar la llamada sin video.
          newTrack = await requestVideoTrack(facingMode, false);
          appliedMode = facingMode;
        } catch (err3) {
          debugError('❌ Tampoco se pudo recuperar la camara anterior:', err3);
          setSwitchingCamera(false);
          alert('No se pudo cambiar de cámara');
          return;
        }
      }
    }

    newTrack.enabled = videoEnabled;

    // replaceTrack sobre el sender: no requiere renegociar SDP, el peer remoto
    // ve el cambio de camara sin cortes. Debe ir antes de mutar el stream
    // porque simple-peer localiza el sender por (trackAnterior, stream).
    if (connectionRef.current) {
      try {
        connectionRef.current.replaceTrack(oldTrack, newTrack, currentStream);
      } catch (err) {
        debugError('Error reemplazando el track de video en el peer:', err);
      }
    }

    currentStream.removeTrack(oldTrack);
    currentStream.addTrack(newTrack);

    if (myVideo.current) {
      myVideo.current.srcObject = currentStream;
      const playPromise = myVideo.current.play?.();
      if (playPromise?.catch) playPromise.catch(() => {});
    }

    setFacingMode(appliedMode);
    setSwitchingCamera(false);
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  const otherName = otherUser.user?.profile?.firstName || otherUser.profile?.firstName || 'Participante';

  const statusLabel = connectionHealth === 'reconnecting'
    ? '🔄 Reconectando...'
    : connectionHealth === 'failed'
      ? '❌ Conexión perdida'
      : callAccepted ? '✅ En llamada' : isCalling ? '📞 Llamando...' : '⏳ Esperando...';

  const statusClass = connectionHealth === 'reconnecting'
    ? 'text-yellow-400'
    : connectionHealth === 'failed'
      ? 'text-red-400'
      : 'text-gray-400';

  return (
    <div
      className={
        minimized
          ? 'fixed bottom-4 right-4 z-50 w-40 sm:w-56 rounded-lg overflow-hidden shadow-2xl border-2 border-gray-600'
          : 'fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50'
      }
    >
      <div className={minimized ? 'w-full flex flex-col bg-gray-900' : 'w-full h-full flex flex-col'}>

        {/* Header */}
        <div className={`bg-gray-900 text-white p-4 flex justify-between items-center ${minimized ? 'hidden' : ''}`}>
          <div>
            <h2 className="text-xl font-semibold">
              Videollamada con {otherName}
            </h2>
            <p className={`text-sm ${statusClass}`}>
              {statusLabel}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMinimized(true)}
              title="Minimizar video"
              className="text-white hover:text-gray-300"
            >
              <Minimize2 className="h-6 w-6" />
            </button>
            <button type="button" onClick={onClose} className="text-white hover:text-gray-300">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Video area */}
        <div className={`relative bg-black ${minimized ? 'h-28 sm:h-36' : 'flex-1'}`}>

          {/* Video remoto — siempre en el DOM para que el ref exista */}
          <video
            ref={userVideo}
            autoPlay
            playsInline
            className={`w-full h-full object-cover ${
              callAccepted && !callEnded ? 'block' : 'hidden'
            }`}
          />

          {/* Overlay de reconexión: se muestra sobre el video sin destruirlo */}
          {callAccepted && !callEnded && connectionHealth === 'reconnecting' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white">
              <div className="text-center px-4">
                <p className="text-lg font-semibold">🔄 Conexión inestable, reconectando…</p>
                <p className="text-sm text-gray-300 mt-1">
                  No cierres la llamada, esto puede tardar unos segundos
                </p>
              </div>
            </div>
          )}

          {/* Placeholder cuando no hay llamada activa */}
          {(!callAccepted || callEnded) && (
            <div className="absolute inset-0 flex items-center justify-center text-white">
              <div className="text-center">
                <Video className="h-24 w-24 mx-auto mb-4 text-gray-600" />
                <p className="text-xl">
                  {receivingCall
                    ? '📞 Llamada entrante'
                    : isCalling
                    ? '⏳ Esperando respuesta'
                    : '🔄 Esperando conexión'}
                </p>
                {callError && (
                  <p className="mt-3 max-w-sm mx-auto text-sm text-red-300 bg-red-900 bg-opacity-40 rounded-lg px-4 py-2">
                    {callError}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Overlay en modo minimizado: nombre + botón maximizar */}
          {minimized && (
            <div className="absolute inset-0 flex flex-col justify-between p-1 pointer-events-none">
              <div className="flex justify-end pointer-events-auto">
                <button
                  type="button"
                  onClick={() => setMinimized(false)}
                  title="Maximizar video"
                  className="bg-black bg-opacity-60 text-white p-1 rounded hover:bg-opacity-80"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
              </div>
              <div className="pointer-events-auto self-start bg-black bg-opacity-60 text-white text-xs px-2 py-0.5 rounded max-w-full truncate">
                {otherName}
              </div>
            </div>
          )}

          {/* Video propio (PiP) */}
          {stream && (
            <div className={`absolute top-4 right-4 w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-36 lg:w-64 lg:h-48 bg-gray-800 rounded-lg overflow-hidden shadow-2xl border-2 border-gray-600 ${minimized ? 'hidden' : ''}`}>
              {/* El espejo es la convencion para la camara frontal; con la
                  trasera invertiria lo que el usuario esta apuntando. */}
              <video
                ref={myVideo}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ transform: facingMode === 'environment' ? 'none' : 'scaleX(-1)' }}
              />
              <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                Tú
              </div>
            </div>
          )}

          {/* Overlay llamada entrante */}
          {receivingCall && !callAccepted && (
            <div className="absolute inset-0 bg-black bg-opacity-80 flex items-center justify-center">
              <div className="bg-white rounded-lg p-8 text-center max-w-md">
                <Video className="h-16 w-16 mx-auto mb-4 text-blue-600" />
                <h3 className="text-2xl font-bold mb-2">Llamada entrante</h3>
                <p className="text-gray-600 mb-6">Te están llamando</p>
                <div className="flex gap-4 justify-center">
                  <button
                    type="button"
                    onClick={answerCall}
                    className="px-8 py-3 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
                  >
                    <Phone className="h-5 w-5" /> Aceptar
                  </button>
                  <button
                    type="button"
                    onClick={handleEndCall}
                    className="px-8 py-3 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
                  >
                    <PhoneOff className="h-5 w-5" /> Rechazar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Controles */}
        <div className={`bg-gray-900 p-6 flex justify-center gap-4 ${minimized ? 'hidden' : ''}`}>
          <button
            type="button"
            onClick={() => setMinimized(true)}
            title="Minimizar video"
            className="p-4 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-colors"
          >
            <Minimize2 className="h-6 w-6" />
          </button>

          <button
            type="button"
            onClick={toggleVideo}
            className={`p-4 rounded-full transition-colors ${
              videoEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-700'
            } text-white`}
          >
            {videoEnabled ? <Video className="h-6 w-6" /> : <VideoOff className="h-6 w-6" />}
          </button>

          <button
            type="button"
            onClick={toggleAudio}
            className={`p-4 rounded-full transition-colors ${
              audioEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-700'
            } text-white`}
          >
            {audioEnabled ? <Mic className="h-6 w-6" /> : <MicOff className="h-6 w-6" />}
          </button>

          {/* Solo en moviles con mas de una camara */}
          {canSwitchCamera && (
            <button
              type="button"
              onClick={switchCamera}
              disabled={switchingCamera || !stream}
              title={facingMode === 'user' ? 'Usar cámara trasera' : 'Usar cámara frontal'}
              aria-label={facingMode === 'user' ? 'Usar cámara trasera' : 'Usar cámara frontal'}
              className="p-4 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-colors disabled:opacity-50"
            >
              <SwitchCamera className="h-6 w-6" />
            </button>
          )}

          {!callAccepted && !receivingCall ? (
            <button
              type="button"
              onClick={callUser}
              title="Llamar"
              disabled={isCalling || !stream}
              className="p-4 rounded-full bg-green-600 hover:bg-green-700 text-white transition-colors disabled:opacity-50"
            >
              <Phone className="h-6 w-6" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleEndCall}
              title="Colgar"
              className="p-4 rounded-full bg-red-600 hover:bg-red-700 text-white transition-colors"
            >
              <PhoneOff className="h-6 w-6" />
            </button>
          )}
        </div>

      </div>
    </div>
  );
}

export default VideoCallModal;
